import { FC } from 'react';

import { CapQuestLogo, LinkedInIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { StringKey } from '../../../lang/index';
import { LandingSolutionPageRoute, PageRoute, TermsRoute } from '../../../types/page.types';

/** Without a base the hrefs would be app-relative and 404 for an external viewer. */
const landingUrl = process.env.REACT_APP_LANDING_URL || 'https://capquest.io';

type FooterLink = {
  href: string;
  label: StringKey;
};

type FooterColumn = {
  links: FooterLink[];
  title: StringKey;
};

const solution = (slug: LandingSolutionPageRoute) => `${landingUrl}/solutions/${slug}`;

/** Templates is an app page, so it is reached on the origin already serving this one. */
const templatesUrl = `${window.location.origin}${PageRoute.LIBRARY}`;

/** Pricing, Guides and FAQs are absent on purpose: the landing has no route for them. */
const COLUMNS: FooterColumn[] = [
  {
    links: [
      { href: `${landingUrl}/about`, label: 'SHARE_FOOTER_ABOUT' },
      { href: `${landingUrl}/perks`, label: 'SHARE_FOOTER_PERKS' },
    ],
    title: 'SHARE_FOOTER_COMPANY',
  },
  {
    links: [
      {
        href: solution(LandingSolutionPageRoute.EQUITY_PLAN_AUTOMATION),
        label: 'SHARE_FOOTER_ESOP',
      },
      {
        href: solution(LandingSolutionPageRoute.OPTIMIZED_FUNDRAISING_INSTRUMENTS),
        label: 'SHARE_FOOTER_FINANCIAL_TOOLS',
      },
      {
        href: solution(LandingSolutionPageRoute.SIMPLIFIED_CAP_TABLE),
        label: 'SHARE_FOOTER_CAP_TABLE',
      },
      {
        href: solution(LandingSolutionPageRoute.ENHANCED_INVESTOR_RELATIONS),
        label: 'SHARE_FOOTER_INVESTOR_RELATIONS',
      },
      {
        href: solution(LandingSolutionPageRoute.EFFORTLESS_SCENARIO_MODELING),
        label: 'SHARE_FOOTER_SCENARIO_MODELING',
      },
    ],
    title: 'SHARE_FOOTER_SOLUTIONS',
  },
  {
    links: [{ href: templatesUrl, label: 'SHARE_FOOTER_TEMPLATES' }],
    title: 'SHARE_FOOTER_RESOURCES',
  },
  {
    links: [
      { href: `${landingUrl}${TermsRoute.TERMS_OF_SERVICE}`, label: 'SHARE_FOOTER_TERMS' },
      { href: `${landingUrl}${TermsRoute.PRIVACY_POLICY}`, label: 'SHARE_FOOTER_PRIVACY' },
      { href: `${landingUrl}${TermsRoute.COOKIE_POLICY}`, label: 'SHARE_FOOTER_COOKIES' },
    ],
    title: 'SHARE_FOOTER_LEGAL',
  },
  {
    links: [{ href: `${landingUrl}/book-demo`, label: 'SHARE_FOOTER_TALK_TO_SALES' }],
    title: 'SHARE_FOOTER_CONTACT',
  },
];

const SOCIALS = [
  { href: 'https://www.linkedin.com/company/104307332', icon: <LinkedInIcon />, label: 'LinkedIn' },
];

export const PublicFooter: FC = () => (
  // Inset to the same width the table above it gets — the app's collapsed sidebar on the left and
  // its activity rail on the right — so the page has one edge rather than two.
  <footer className="px-4 pb-6 lg:pl-[72px] lg:pr-24">
    <div className="flex flex-col gap-10 rounded-xl bg-white p-8 shadow-sm">
      <div className="flex flex-col gap-8 xs:grid xs:grid-cols-3 xs:gap-x-8 lg:flex lg:flex-row lg:flex-wrap lg:gap-x-20">
        {COLUMNS.map(({ links, title }) => (
          <div className="flex flex-col gap-4" key={title}>
            <span className="text-base font-[600] text-[#172335]">
              <AppFormattedMessage id={title} />
            </span>
            <div className="flex flex-col gap-2">
              {links.map(({ href, label }) => (
                <a
                  className="text-sm font-[450] text-gray-600 transition-colors hover:text-gray-800"
                  href={href}
                  key={label}
                  rel="noreferrer"
                  target="_blank"
                >
                  <AppFormattedMessage id={label} />
                </a>
              ))}
            </div>
          </div>
        ))}

        <div className="order-first flex flex-col gap-4 xs:order-none lg:ml-auto">
          <span className="text-sm font-[450] text-gray-500">
            <AppFormattedMessage id="SHARE_PUBLIC_POWERED_BY" />
          </span>
          <CapQuestLogo mode="full" />
        </div>
      </div>

      {/* An empty first track: the copyright is centred on the footer, not on what the icons leave. */}
      <div className="flex flex-col items-center gap-4 border-t border-gray-100 pt-6 xs:grid xs:grid-cols-3">
        <span className="hidden xs:order-1 xs:block" />

        <div className="flex justify-center gap-4 xs:order-3 xs:justify-end">
          {SOCIALS.map(({ href, icon, label }) => (
            <a aria-label={label} href={href} key={label} rel="noreferrer" target="_blank">
              {icon}
            </a>
          ))}
        </div>

        <span className="text-center text-xs font-[450] text-gray-400 xs:order-2">
          <AppFormattedMessage
            id="SHARE_FOOTER_COPYRIGHT"
            values={{ year: new Date().getFullYear() }}
          />
        </span>
      </div>
    </div>
  </footer>
);
